import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export function useUnreadTicketReplies() {
  const { user } = useAuth();
  const [hasUnreadReplies, setHasUnreadReplies] = useState(false);
  const [loading, setLoading] = useState(true);
  // Unique per hook instance: multiple components use this hook at the same time
  // and sharing a channel name breaks the realtime subscription.
  const channelIdRef = useRef(Math.random().toString(36).slice(2));

  const checkUnreadReplies = useCallback(async () => {
    if (!user) {
      setHasUnreadReplies(false);
      setLoading(false);
      return;
    }

    try {
      // Get user's tickets with last_viewed_at
      const { data: tickets, error: ticketsError } = await supabase
        .from('support_tickets')
        .select('id, last_viewed_at')
        .eq('user_id', user.id);

      if (ticketsError) throw ticketsError;

      if (!tickets || tickets.length === 0) {
        setHasUnreadReplies(false);
        setLoading(false);
        return;
      }

      // Check each ticket for replies after last_viewed_at
      let hasUnread = false;
      for (const ticket of tickets) {
        // Use the public view that hides admin_id to prevent admin identity exposure
        const { data: replies, error: repliesError } = await supabase
          .from('ticket_replies_public' as any)
          .select('id, created_at')
          .eq('ticket_id', ticket.id)
          .gt('created_at', ticket.last_viewed_at || '1970-01-01')
          .limit(1);

        if (repliesError) throw repliesError;

        if (replies && replies.length > 0) {
          hasUnread = true;
          break;
        }
      }
      
      setHasUnreadReplies(hasUnread);
    } catch (error) {
      console.error('Error checking unread replies:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkUnreadReplies();
  }, [checkUnreadReplies]);

  // Subscribe to realtime updates for ticket_replies
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`ticket-replies-realtime-${channelIdRef.current}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_replies'
        },
        () => {
          checkUnreadReplies();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, checkUnreadReplies]);

  // Function to mark a ticket as viewed
  const markTicketAsViewed = useCallback(async (ticketId: string) => {
    if (!user) return;

    try {
      await supabase
        .from('support_tickets')
        .update({ last_viewed_at: new Date().toISOString() })
        .eq('id', ticketId)
        .eq('user_id', user.id);

      // Recheck after marking as viewed
      checkUnreadReplies();
    } catch (error) {
      console.error('Error marking ticket as viewed:', error);
    }
  }, [user, checkUnreadReplies]);

  return {
    hasUnreadReplies,
    loading,
    refetch: checkUnreadReplies,
    markTicketAsViewed
  };
}
